// Although the replacement for `src/components/App/App.tsx`
// is in `src/rxdb/replication-v2/App/App.tsx`,
// most of the logic for that replaced component lives here.
import '../../../components/App/App.css';
import './dayjsExtensions';

import DayjsUtils from '@date-io/dayjs';
import { MuiPickersUtilsProvider } from '@material-ui/pickers';
import { setContext } from '@sentry/react';
import { Auth } from 'aws-amplify';
import dayjs from 'dayjs';
import React, { useContext, useEffect, useState } from 'react';
import { BrowserRouter as Router, Redirect, Route, Switch } from 'react-router-dom';

import { configCat } from '@/featureFlags/ConfigCat';
import { useBackgroundRefresh } from '@/rxdb/replication-v2/App/useBackgroundRefresh';
import { useTokenRefresh } from '@/rxdb/replication-v2/App/useTokenRefresh';
import { useRxDB } from '@/rxdb/useRxDB';
import { getBorerShortName } from '@/utilities/getBorerShortName';
import {
  getUserEmailForTracking,
  NO_EMAIL_FOUND,
  TrackingEventType,
  useEventTracking,
} from '@/utilities/hooks/useEventTracking';
import { DifferentSiteIdError } from '@/utilities/minesightErrors';

import AddAreaCheck from '../../../components/AddAreaCheck';
import AddWorkFaceAreaInspection from '../../../components/AddWorkFaceAreaInspection';
import AppBarController from '../../../components/AppBarController';
import AreaCheckPage from '../../../components/AreaCheckPage';
import { BorerHeartbeat } from '../../../components/BorerHeartbeat/BorerHeartbeat';
import { useBorerHeartbeat } from '../../../components/BorerHeartbeat/useBorerHeartbeat';
import CacheUpdateHandler from '../../../components/CacheUpdateHandler';
import Home from '../../../components/Home';
import Advances from '../../../components/pages/Advances';
import Equipment from '../../../components/pages/Equipment';
import Login from '../../../components/pages/Login';
import PiSightPage from '../../../components/pages/PiSightPage';
import ShiftEnd from '../../../components/pages/ShiftEnd';
import PWAUpdate from '../../../components/PWAUpdate';
import ShiftStart from '../../../components/ShiftStart';
import Signatures from '../../../components/Signatures';
import TabBar from '../../../components/TabBar';
import { PING_RESOURCE_BIG } from '../../../components/UpdateModal/UpdateModal';
import { useMst } from '../../../mobx-models/Root';
import { useNotification, useOnlineStatus } from '../../../utilities';
import { useLastSyncTime } from '../../../utilities/hooks/useLastSyncTime';
import { useWindowVisibility } from '../../../utilities/hooks/useWindowVisibility';
import { getLastSyncDiff } from '../../../utilities/syncHelpers';
import { RxdbCollectionName } from '../../rxdbCollectionName';
import Loading from '../Loading/Loading';
import { logError } from '../logError';
import { userEmailManagementContext } from '../userEmailManagementContext';
import { useRestart } from '../useRestart';
import { Logout } from './Logout';

export const UnobservedApp: React.FC = () => {
  const { db } = useRxDB();
  const featureFlagManager = useContext(configCat.featureFlagManagement.context);
  setContext('featureFlags', featureFlagManager);

  const online = useOnlineStatus();
  const { equipment, user } = useMst();

  const lastBorerStateSyncTime = useLastSyncTime(RxdbCollectionName.BORER_OPERATOR_STATE_FEED);
  const windowVisible = useWindowVisibility();
  const { sendBorerHeartbeat } = useBorerHeartbeat();
  const { trackEvent } = useEventTracking();
  const { errorNotification } = useNotification();
  const { location } = window;

  const [showLoadingModal, setShowLoadingModal] = useState(false);

  useBackgroundRefresh();
  useTokenRefresh();
  useRestart();

  const checkIfAuthenticated = async () => {
    if (location.pathname.indexOf('login') !== -1) return; // We already check if authenticated on Login
    try {
      //  Get session (Error thrown if no session exists)
      await Auth.currentAuthenticatedUser({ bypassCache: true });
      await user.getProfile();
      // Setup user in sentry and flag provider
      // `any` to support legacy extraction of `err` properties.
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      if (error?.message === 'Network Error' || error?.message === 'Load Failed') {
        const errorDetails = {
          redirectToLogin: false,
          email: getUserEmailForTracking(user?.Profile?.email ?? NO_EMAIL_FOUND),
        };
        logError('Network error checking authentication', error, errorDetails);

        await trackEvent(TrackingEventType.AUTHENTICATION_NETWORK_LOSS, {
          err: error,
          ...errorDetails,
        });
        // Don't re-direct to login if network issues / offline
        return;
      }
      if (error === 'The user is not authenticated') {
        // This occurs for several reasons
        // 1. Refresh token expiry
        // 2. No session exists (never logged in)
        // 3. Session revoked from Cognito
        const finalNetworkCheck = await fetch(PING_RESOURCE_BIG, {
          method: 'GET',
        });
        if (finalNetworkCheck.status !== 200) return;
        try {
          // Good network, attempt refresh one last time
          const cognitoUser = await Auth.currentAuthenticatedUser();
          const currentSession = await Auth.currentSession();
          const refreshToken = currentSession.getRefreshToken();
          cognitoUser.refreshSession(refreshToken);
        } catch (innerError) {
          if (innerError === 'The user is not authenticated') {
            // Likely due to revoked token
            errorNotification(
              'There was an authentication error, you will be re-directed to the login screen.',
            );
            const errorDetails = {
              shortName: getBorerShortName(),
              redirectToLogin: true,
              userNotAuthenticatedError: true,
            };
            logError('Error during refresh after authentication error', error, errorDetails);
            await trackEvent(TrackingEventType.BAD_AUTH_TOKEN, {
              err: error,
              ...errorDetails,
            });
            setTimeout(() => {
              location.href = '/logout';
            }, 5000);
          }
        }
        return;
      }
      if (error.code === DifferentSiteIdError.code) {
        errorNotification(DifferentSiteIdError.message);
        await trackEvent(TrackingEventType.MISCONFIGURED_USER, {
          err: error,
          redirectToLogin: true,
        });
        setTimeout(() => {
          location.href = '/logout';
        }, 5000);
        return;
      }
      // Previously would result in redirect to /login
      const errorDetails = {
        unknownAuthenticationError: true,
        redirectToLogin: false,
      };
      logError('Unknown error checking authentication', error, errorDetails);
      await trackEvent(TrackingEventType.UNKNOWN_AUTHENTICATION_ERROR, {
        err: error,
        ...errorDetails,
      });
    }
  };

  useEffect(() => {
    if (location.pathname.indexOf('logout') === -1 && windowVisible && online)
      checkIfAuthenticated();

    //  On opening app, check to see if latest data is available
    const recentSyncSeconds = getLastSyncDiff();
    const syncInterval = 3600;

    if (windowVisible && recentSyncSeconds && recentSyncSeconds >= syncInterval && online) {
      (async () => {
        setShowLoadingModal(true);

        if (db) {
          await db.refreshTokensOnAllCollections();
          await db.runSingleReplication();
          setShowLoadingModal(false);
        }
      })();
    } else if (!online) {
      setShowLoadingModal(false);
    }
  }, [
    // Legacy dependencies
    windowVisible,
    online,
    // Missing dependencies from legacy code:
    //   * checkIfAuthenticated,
    //     * But is asynchronous and heavily dependent on other code in this function.
    //       Rewriting this will be non-trivial.
    //   * db
    //   * location.pathname
    //     Dangerous to set as a dependency given that we do routing below.
  ]);

  const refreshEmail = useContext(userEmailManagementContext).refresh;

  // Legacy Any
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const handleSuccessfulLogin = async (history: any) => {
    try {
      await refreshEmail();
      await equipment.fetchBorerList();
      sendBorerHeartbeat({ loginTime: dayjs(), lastUserInteractionTime: dayjs() });
      history.push('/home');
    } catch (error) {
      logError('Error handling login', error, {});
    }
  };

  return (
    <div
      id="app-root"
      style={{ touchAction: 'none' }}
      onTouchStart={e => {
        e.preventDefault();
      }}
    >
      <MuiPickersUtilsProvider utils={DayjsUtils}>
        <CacheUpdateHandler />
        <PWAUpdate serviceWorkerFile="sw.js" />
        <Router>
          <BorerHeartbeat />
          <AppBarController loadingModalOpen={showLoadingModal} />
          <Switch>
            <Route path="/initializing" exact>
              <Loading />
            </Route>
            <Route path="/home" exact>
              <Home />
            </Route>
            <Route path="/signatures" exact>
              <Signatures />
            </Route>
            <Route path="/shift-start" exact>
              <ShiftStart />
            </Route>
            <Route path="/area-check" exact>
              <AreaCheckPage />
            </Route>
            <Route path="/add-areacheck" exact>
              <AddAreaCheck />
            </Route>
            <Route path="/add-wfai" exact>
              <AddWorkFaceAreaInspection />
            </Route>
            <Route path="/equipment" exact>
              <Equipment />
            </Route>
            <Route path="/delaysandactivities" exact>
              <PiSightPage lastSyncTime={lastBorerStateSyncTime} />
            </Route>
            <Route path="/advances" exact>
              <Advances />
            </Route>
            <Route path="/shift-end">
              <ShiftEnd />
            </Route>
            <Route path="/login" exact>
              <Login loginHandler={handleSuccessfulLogin} />
            </Route>
            <Route path="/logout" exact>
              <Logout />
            </Route>
            <Route path="/">
              {/* siteId set after initial login and bootstrapped from localStorage */}
              {user.siteId ? <Redirect to="/initializing" /> : <Redirect to="/login" />}
            </Route>
          </Switch>
          <TabBar />
        </Router>
      </MuiPickersUtilsProvider>
    </div>
  );
};
